import { ENUM_LABELS, type WorklistRow, PIN_PREFIX, reportProgress } from '#shared/types';
import type { Column } from '#client/components/ui/DataTable';
import { DueCell } from '#client/components/ui/DueCell';
import { Identifier } from '#client/components/ui/Identifier';
import { Progress } from '#client/components/ui/Progress';
import { PaymentStatusBadge, ReportStatusBadge } from '#client/components/ui/StatusBadge';
import { formatAgeShort, formatDateTime } from '#client/lib/format';

const LEADING_COLUMNS: Column<WorklistRow>[] = [
  {
    key: 'pin',
    header: 'PIN',
    frozen: true,
    render: (row) => <Identifier value={row.patient.pin_no} stripPrefix={PIN_PREFIX} />,
    width: '176px',
    skeletonWidth: '120px',
  },
  {
    key: 'patient',
    header: 'Patient',
    render: (row) => (
      <span>
        <strong>{row.patient.name}</strong>{' '}
        <span>
          {formatAgeShort(row.patient.age_year, row.patient.age_month, row.patient.age_days)}{' '}
          {ENUM_LABELS.gender[row.patient.gender].charAt(0)}
        </span>
      </span>
    ),
    skeletonWidth: '10rem',
  },
  { key: 'test', header: 'Test', render: (row) => row.name, skeletonWidth: '8rem' },
];

const COMPLETED_COLUMNS: Column<WorklistRow>[] = [
  {
    key: 'completed_at',
    header: 'Completed',
    render: (row) => <span className="tnum">{formatDateTime(row.completed_at)}</span>,
    width: '190px',
  },
];

const OPEN_COLUMNS: Column<WorklistRow>[] = [
  {
    key: 'progress',
    header: 'Results',
    render: (row) => <Progress {...reportProgress(row)} />,
    width: '130px',
  },
  {
    key: 'due',
    header: 'Due',
    render: (row) => <DueCell {...row} />,
    width: '150px',
  },
];

const TRAILING_COLUMNS: Column<WorklistRow>[] = [
  {
    key: 'payment',
    header: 'Payment',
    render: (row) => <PaymentStatusBadge status={row.patient.payment_status} />,
    width: '120px',
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => <ReportStatusBadge status={row.report_status} />,
    width: '130px',
  },
];

/**
 * The worklist's columns. Two of them swap by tab: open work is about progress
 * and deadlines, finished work is about when it was finished.
 */
export function buildWorklistColumns(isCompletedTab: boolean): Column<WorklistRow>[] {
  return [
    ...LEADING_COLUMNS,
    ...(isCompletedTab ? COMPLETED_COLUMNS : OPEN_COLUMNS),
    ...TRAILING_COLUMNS,
  ];
}
